import { computed, ref, type ComputedRef } from "vue";
import { usePage } from "@inertiajs/vue3";
import type {
    IAiChatAccess,
    IRhizomPageProps,
} from "@/types/RhizomPageProps/RhizomPageProps";

/**
 * Live AI-chat access snapshot shared by every consumer (sidebar trial counter,
 * widget composer). Inertia shares the server value on page load, but the widget
 * sends messages over plain JSON endpoints, so the shared prop would stay stale
 * until the next full visit. Those endpoints return a fresh snapshot which is
 * published here, and every consumer re-renders instantly.
 *
 * The published snapshot only outranks the shared prop for as long as the prop
 * itself does not move: the moment the server sends a different value (a new
 * visit, a team switch) that value is authoritative again. The baseline carries
 * the team id, so switching to a team whose usage happens to match the one this
 * snapshot was published against cannot inherit the other team's count.
 */
const liveAccess = ref<IAiChatAccess | null>(null);
const liveBaseline = ref<string | null>(null);

const serialize = (
    teamId: number | null,
    access: IAiChatAccess | null,
): string => JSON.stringify({ teamId, access });

const isAccess = (value: unknown): value is IAiChatAccess =>
    value !== null && typeof value === "object" && "level" in value;

export function useAiChatAccess(): {
    access: ComputedRef<IAiChatAccess | null>;
    publishAccess: (value: unknown) => void;
} {
    const page = usePage<IRhizomPageProps>();

    const sharedAccess = computed<IAiChatAccess | null>(
        () => page.props.aiChat ?? null,
    );

    const sharedBaseline = computed<string>(() =>
        serialize(page.props.team?.id ?? null, sharedAccess.value),
    );

    const access = computed<IAiChatAccess | null>(() => {
        if (liveAccess.value === null) {
            return sharedAccess.value;
        }

        if (sharedBaseline.value !== liveBaseline.value) {
            return sharedAccess.value;
        }

        return liveAccess.value;
    });

    const publishAccess = (value: unknown): void => {
        if (isAccess(value) === false) {
            return;
        }

        liveBaseline.value = sharedBaseline.value;
        liveAccess.value = value;
    };

    return { access, publishAccess };
}
